import { Group, Stack } from "@mantine/core"

import type { SourceContextGraphNodeDto } from "~/gen/api/types"
import {
  HorizontalDivider,
  VerticalDivider,
} from "~/pages/Brand/Products/Details/TraceabilityGraph/Divider"
import { TraceabilityTreeBranch } from "~/pages/Brand/Products/Details/TraceabilityGraph/TraceabilityTreeBranch"
import { NO_ACTIVITY_NAME } from "~/utils/operations"

import {
  getVerticalDividerCoveredHeight,
  shouldRenderChildDivider,
} from "./product-traceability.utils"
import {
  buildForkOriginCue,
  getRenderedChildren,
  isNodeWithoutActivity,
  isSourceContextGraphNode,
  type BrandTraceabilityGraphNode,
} from "./traceability-graph.view-model"

type ProductTraceabilityTreeProps = {
  readonly item: BrandTraceabilityGraphNode
  readonly nodesById: Map<string, BrandTraceabilityGraphNode>
  readonly isFirstChild?: boolean
  readonly parentDividerRendered?: boolean
  readonly ancestorReferences: string[]
  readonly translateItemType: (type: SourceContextGraphNodeDto["type"]) => string
  readonly translateActivity: (activityName: string) => string
}

const SYNTHETIC_ROOT_PREFIX = "synthetic-root-"

export const ProductTraceabilityTree = ({
  item,
  nodesById,
  isFirstChild = true,
  parentDividerRendered = false,
  ancestorReferences,
  translateItemType,
  translateActivity,
}: ProductTraceabilityTreeProps) => {
  const renderedChildren = getRenderedChildren(item, nodesById)
  const shouldRenderCurrentNode = !isNodeWithoutActivity(item)
  const nextAncestorReferences = [...ancestorReferences, item.reference]
  const renderLeadingDividerInTree = !isFirstChild && !parentDividerRendered
  const isSyntheticRoot =
    item.id.startsWith(SYNTHETIC_ROOT_PREFIX) &&
    item.activityName === NO_ACTIVITY_NAME

  if (isSyntheticRoot) {
    return (
      <Stack
        align="flex-start"
        justify="left"
        style={{ position: "relative" }}
      >
        <VerticalDivider
          height={getVerticalDividerCoveredHeight(renderedChildren, nodesById)}
        />
        {renderedChildren.map(({ node: childNode, hiddenAncestors }) => {
          return (
            <Group key={childNode.id} gap={0} align="flex-start" wrap="nowrap">
              <HorizontalDivider />
              <ProductTraceabilityTree
                item={childNode}
                nodesById={nodesById}
                isFirstChild={false}
                parentDividerRendered
                ancestorReferences={[
                  ...nextAncestorReferences,
                  ...hiddenAncestors.map((ancestor) => ancestor.reference),
                ]}
                translateItemType={translateItemType}
                translateActivity={translateActivity}
              />
            </Group>
          )
        })}
      </Stack>
    )
  }

  if (!shouldRenderCurrentNode) {
    return (
      <Stack align="flex-start" justify="left">
        {renderedChildren.map(
          ({ node: childNode, hiddenAncestors }, childIndex) => {
            const siblingDividerRendered = childIndex > 0

            return (
              <Group key={childNode.id} gap={0} align="flex-start" wrap="nowrap">
                {siblingDividerRendered && <HorizontalDivider />}
                <ProductTraceabilityTree
                  item={childNode}
                  nodesById={nodesById}
                  isFirstChild={false}
                  parentDividerRendered={siblingDividerRendered}
                  ancestorReferences={[
                    ...nextAncestorReferences,
                    ...hiddenAncestors.map((ancestor) => ancestor.reference),
                  ]}
                  translateItemType={translateItemType}
                  translateActivity={translateActivity}
                />
              </Group>
            )
          }
        )}
      </Stack>
    )
  }

  return (
    <Group
      gap={0}
      align="flex-start"
      style={{ position: "relative" }}
      wrap="nowrap"
    >
      {renderLeadingDividerInTree && <HorizontalDivider />}
      <TraceabilityTreeBranch
        node={item}
        hideFirstDivider={renderLeadingDividerInTree || parentDividerRendered}
        parentReferences={ancestorReferences}
        originCue={
          isSourceContextGraphNode(item)
            ? buildForkOriginCue({
                forkRoot: item,
                translateItemType,
                translateActivity,
              })
            : undefined
        }
      />
      {renderedChildren.length > 0 && (
        <Stack align="flex-start" justify="left">
          <VerticalDivider
            height={getVerticalDividerCoveredHeight(renderedChildren, nodesById)}
          />
          {renderedChildren.map(({ node: childNode, hiddenAncestors }, childIndex) => {
            const shouldShowDivider = shouldRenderChildDivider(
              item,
              childNode,
              childIndex
            )

            return (
              <Group key={childNode.id} gap={0} align="flex-start" wrap="nowrap">
                {shouldShowDivider && <HorizontalDivider />}
                <ProductTraceabilityTree
                  item={childNode}
                  nodesById={nodesById}
                  isFirstChild={false}
                  parentDividerRendered={shouldShowDivider}
                  ancestorReferences={[
                    ...nextAncestorReferences,
                    ...hiddenAncestors.map((ancestor) => ancestor.reference),
                  ]}
                  translateItemType={translateItemType}
                  translateActivity={translateActivity}
                />
              </Group>
            )
          })}
        </Stack>
      )}
    </Group>
  )
}
